import React, { useEffect, useState } from 'react';
import { Pencil, Trash2, X, Save, UserPlus2, AlertCircle } from 'lucide-react';
import { adminApi, api } from '../../lib/api';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';

export default function AdminUsers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const { user: currentUser } = useAuth();

  const refresh = () => {
    setLoading(true);
    adminApi.users().then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const startEdit = (u) => {
    setEditing({ ...u, password: '' });
    setError('');
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      const body = {
        name: editing.name, email: editing.email, role: editing.role,
        nie: editing.nie || '', country: editing.country || '', phone: editing.phone || '',
      };
      if (editing.password) body.password = editing.password;
      await api.put(`/admin/users/${editing.id}`, body);
      setEditing(null);
      refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not save');
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? This also removes their quotes, policies and Insurance-uploaded documents.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      refresh();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Could not delete');
    }
  };

  return (
    <div>
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#0b1f3a]">Users</h2>
          <p className="text-slate-500 text-sm mt-1">{items.length} registered accounts.</p>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {loading ? (
          <p className="p-8 text-center text-slate-500 text-sm">Loading...</p>
        ) : items.length === 0 ? (
          <p className="p-8 text-center text-slate-500 text-sm">No users yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Email</th>
                  <th className="text-left px-4 py-3 font-semibold">Phone</th>
                  <th className="text-left px-4 py-3 font-semibold">NIE</th>
                  <th className="text-left px-4 py-3 font-semibold">Country</th>
                  <th className="text-left px-4 py-3 font-semibold">Role</th>
                  <th className="text-left px-4 py-3 font-semibold">Joined</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((u) => (
                  <tr key={u.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 text-slate-700 font-medium whitespace-nowrap">{u.name || '\u2014'}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{u.email}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{u.phone || '\u2014'}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{u.nie || '\u2014'}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{u.country || '\u2014'}</td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                        u.role === 'admin' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                      }`}>{u.role}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '\u2014'}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => startEdit(u)} className="text-slate-500 hover:text-[#0d3a8a] mr-3" title="Edit"><Pencil size={16} /></button>
                      <button
                        onClick={() => remove(u)}
                        disabled={u.id === currentUser?.id}
                        className={`text-slate-400 hover:text-rose-600 ${u.id === currentUser?.id ? 'opacity-30 cursor-not-allowed' : ''}`}
                        title={u.id === currentUser?.id ? 'You cannot delete your own account' : 'Delete'}
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit dialog */}
      {editing && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl p-6 max-w-lg w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <span className="icon-tile !w-9 !h-9"><UserPlus2 size={16} /></span>
                <h3 className="font-semibold text-[#0b1f3a]">Edit user</h3>
              </div>
              <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
            </div>

            {error && (
              <div className="mb-4 flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-3 py-2 text-sm">
                <AlertCircle size={16} className="mt-0.5" /> {error}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="field-label">Name</Label>
                <Input value={editing.name || ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div className="md:col-span-2">
                <Label className="field-label">Email</Label>
                <Input type="email" value={editing.email || ''} onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
              </div>
              <div>
                <Label className="field-label">Phone</Label>
                <Input value={editing.phone || ''} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} />
              </div>
              <div>
                <Label className="field-label">NIE / Passport</Label>
                <Input value={editing.nie || ''} onChange={(e) => setEditing({ ...editing, nie: e.target.value })} />
              </div>
              <div>
                <Label className="field-label">Country</Label>
                <Input value={editing.country || ''} onChange={(e) => setEditing({ ...editing, country: e.target.value })} />
              </div>
              <div>
                <Label className="field-label">Role</Label>
                <Select value={editing.role} onValueChange={(v) => setEditing({ ...editing, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">User</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label className="field-label">Set new password (leave empty to keep)</Label>
                <Input type="password" value={editing.password || ''} onChange={(e) => setEditing({ ...editing, password: e.target.value })} placeholder="Minimum 6 characters" />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-outline">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-60">
                <Save size={16} /> {saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
